/**
 * Alpine wiring: one store, plus the three blocks that own local state.
 *
 * Component scope is lexical and a store is not, which is what lets each
 * template be its own root: the form in header.php, the results in search.php,
 * the feedback bar under the article, none aware of the others. State that
 * crosses blocks goes in the store; everything else stays in the block that
 * owns it. Most templates need neither and carry a bare `x-data`.
 *
 * The store is the client. `src/js/core/` holds pure helpers, one stream reader
 * and the IO modules, all called from here. There is no framework-agnostic
 * layer underneath and no second copy of the state.
 *
 * The plugin does not enqueue Alpine (see the README). Everything below
 * registers on `alpine:init`, so this file has to run before whatever calls
 * `Alpine.start()`. That is the handle's job rather than this file's: it is
 * registered blocking, which puts it ahead of every deferred script and every
 * `type="module"` tag on the page, the ones no dependency could reach
 * included. src/Components.php has the argument.
 *
 * Same ordering is why `window.Alpine` is read inside the handler and
 * `alpinejs` imported for types only: Rollup evaluates an IIFE's externals at
 * the bottom of the bundle, before Alpine exists, so a captured binding stays
 * `undefined`. `vite.config.ts` has the tripwire.
 *
 * When it goes wrong anyway the page renders blank, so the bottom of this file
 * spends three console errors saying which of the three ways it was.
 */

import type { Alpine as AlpineType } from "alpinejs";

import { conversion, initAnalytics } from "./core/analytics";
import { apiGet, postJson } from "./core/http";
import {
  emptyParams,
  paramsFromUrl,
  toQueryString,
  writeToUrl,
} from "./core/params";
import { insertResult } from "./core/results";
import { parseSummary } from "./core/summary";
import { UIMessageStream } from "./core/uiMessageStream";
import type {
  AiResultData,
  AISearchError,
  FeedbackStatus,
  OverallFeedbackRequest,
  SearchFeedbackMetadata,
  SearchParamValues,
  SearchUIMessagePart,
  SummaryPart,
} from "./core/types";

/**
 * Whether `register()` has run. Every template reads `$store.ai`, so this is
 * the one condition that separates a working page from a blank one, and the
 * three diagnostics at the bottom of this file are all about how it went false.
 */
let registered = false;

/** The search in flight. Module state, because no template renders it. */
let controller: AbortController | null = null;

/**
 * The reasons offered when someone dislikes a response.
 *
 * The `value`s are analytics identifiers the server groups on, so they are
 * neither translated nor ours to reword.
 */
const FEEDBACK_REASONS = [
  { value: "disliked-not-helpful", label: "Not helpful" },
  { value: "disliked-not-factual", label: "Not factually correct" },
  {
    value: "disliked-not-follow-instructions",
    label: "Didn't follow instructions",
  },
  { value: "disliked-offensive-unsafe", label: "Offensive / Unsafe" },
  { value: "disliked-other", label: "Other" },
] as const;

/** The reason value that reveals a free-text box. */
const OTHER_REASON = "disliked-other";

type Panel = "reason" | "overall" | null;

/**
 * State belonging to a response, cleared when a new search starts.
 *
 * A function, not a constant: the arrays and objects must not be shared between
 * resets, or a template mutating `results` would corrupt every future reset.
 */
function emptyResponse() {
  return {
    loading: false,
    /**
     * The query that produced what is on screen, as opposed to the contents of
     * the input, which is `query`. Also what separates "has not searched" from
     * "searched and got nothing"; see the getters at the bottom.
     */
    lastQuery: "",
    /** Last `data-status` from the server, e.g. "Searching", "Analysing". */
    status: "",
    error: null as AISearchError | null,
    messageId: "",
    /** Raw summary text, markers included. */
    summary: "",
    summaryParts: [] as SummaryPart[],
    /** Sorted by relevance, descending. Deduplicated by id. */
    results: [] as AiResultData[],
    feedback: {} as SearchFeedbackMetadata,
    feedbackStatus: "idle" as FeedbackStatus,
  };
}

/**
 * The technical half of an `AISearchError`, off whatever was thrown.
 *
 * Never shown; `errorMessage` words the user's half from the kind. This is for
 * the console and for anything that later reports errors upstream, so an empty
 * or non-`Error` throw still has to say something.
 */
function errorText(e: unknown): string {
  const message = e instanceof Error ? e.message : String(e);
  return message || "Unknown error";
}

// One standalone literal, rather than written inline in the `Alpine.store()`
// call: as an argument it would be contextually typed and `this` would collapse
// to `{}` in the methods and getters below. Spreading in a nested `search` or
// `feedback` object would do the same.
const store = {
  ...emptyResponse(),

  /** Separate from `feedbackStatus`: a different endpoint and a different form. */
  overallFeedbackStatus: "idle" as FeedbackStatus,

  /**
   * The contents of the input. In the store rather than the form because the
   * `?s=` bootstrap and the back button both write it.
   */
  query: "",

  /**
   * The extra search params, for a site that declared any.
   *
   * Templates bind straight to these (`x-model="$store.ai.params.post_type"`),
   * so a control writes the same object the URL restores. Alongside `query`
   * rather than in `emptyResponse()`, since a search does not clear the box it
   * was typed into.
   */
  params: emptyParams() as SearchParamValues,

  /**
   * Which disclosure panel is open. One value rather than a boolean each, so
   * opening either closes the other and no block needs to know how many exist.
   */
  panel: null as Panel,

  reasons: FEEDBACK_REASONS,
  otherReason: OTHER_REASON,

  init(): void {
    // Registered on the store, not per block, which would push one history
    // entry per block per search.
    addEventListener("popstate", () => {
      // The URL rather than `event.state`: every param is written to it, so it
      // is already the complete record.
      const url = new URL(location.href);
      const s = url.searchParams.get("s") ?? "";

      this.close();
      this.params = paramsFromUrl(url);

      if (!s) {
        this.clear();
        return;
      }
      // `run`, not `search`: pushing an entry for the search the back button
      // just asked for gives you a page you cannot navigate away from.
      void this.run(s);
    });

    // Runs during `alpine:init`, before the first DOM walk, so every `x-model`
    // renders its restored value instead of flashing an unfiltered control.
    const url = new URL(location.href);
    this.params = paramsFromUrl(url);

    const fromUrl = url.searchParams.get("s");
    if (fromUrl) {
      // `run` again: the browser is already on this URL, so pushing it would
      // cost a second back press to leave the page.
      void this.run(fromUrl);
    }
  },

  /* ---------- search ---------- */

  /**
   * Runs a query and records it in the URL. This is what templates call.
   *
   * A search already in flight is aborted by `run` and replaced.
   */
  search(query?: string): void {
    // Not a default parameter: a default cannot reference `this`.
    const next = (query ?? this.query).trim();
    if (!next) {
      return;
    }
    this.close();

    // Same rule `url()` applies to a link: replace the params a search owns,
    // leave everything else on the url alone.
    const url = writeToUrl(new URL(location.href), next, this.params);

    history.pushState({ s: next }, "", url);

    void this.run(next);
  },

  /**
   * A url carrying the search that is on screen now, for any link rendered
   * inside the AI search.
   *
   * The store owns the address bar from the first search onwards, so a url PHP
   * printed at page load describes the search the page arrived with, not the
   * one in front of the user. PHP supplies the base, being the part the browser
   * doesn't know, and this fills the query string in from live state:
   *
   *     <a :href="$store.ai.url('<?php echo esc_js( Search::classic_url('') ); ?>')">
   *
   * Parameters on the base survive except the ones a search owns, so a mode
   * flag or a campaign tag is kept.
   *
   * @param base  Url to start from. Relative is resolved against the page.
   * @param query Search term. Defaults to the contents of the input. Pass
   *              `$store.ai.lastQuery` to describe the search that produced
   *              what is on screen instead of the one about to run.
   */
  url(base?: string, query?: string): string {
    return writeToUrl(
      new URL(base || location.href, location.href),
      query ?? this.query,
      this.params,
    ).toString();
  },

  /**
   * Searches, and nothing else. The URL is `search`'s business.
   *
   * Split out so the two callers that must not push a history entry, the `?s=`
   * bootstrap and `popstate`, can call this directly instead of setting a flag
   * `search` has to remember to read. It also adopts the query into the input,
   * so no caller can start a search the box disagrees with.
   */
  async run(query: string): Promise<void> {
    // `abort()` sets the signal synchronously, so from here on
    // `ctl.signal.aborted` answers "has this search been superseded".
    controller?.abort();
    const ctl = new AbortController();
    controller = ctl;

    Object.assign(this, emptyResponse());
    this.loading = true;
    this.status = "Searching";
    this.query = query;
    this.lastQuery = query;

    /* Plain copies of what the stream has produced so far. `this.results` is an
       Alpine reactive proxy, and `parseSummary`/`insertResult` rescan the whole
       array once per chunk, which through the proxy costs a tracked trap per
       element per scan for a read-only pass. The store gets what they return. */
    let summary = "";
    let results: AiResultData[] = [];

    const fail = (next: AISearchError): void => {
      if (!ctl.signal.aborted) {
        this.error = next;
      }
    };

    try {
      // Snapshotted, not read later: a filter changed mid-stream must not
      // re-describe a request that has already gone out.
      const params = toQueryString(query, this.params);
      const res = await apiGet(`/api/search?${params}`, ctl.signal);

      if (!res.ok) {
        fail({
          kind: "http",
          message: `Search failed: ${res.status} ${res.statusText}`,
          status: res.status,
        });
      } else if (!res.body) {
        fail({ kind: "stream", message: "No response body" });
      } else {
        const stream = new UIMessageStream<SearchUIMessagePart>(res.body);

        /* `stream.on` with the abort guard every handler needs already on it,
           so a handler added later cannot be the one that forgets. The guard is
           not redundant with the abort itself: teardown after an abort is
           asynchronous and these writes are not, so a chunk already queued
           downstream can still reach the loop body and land on the state of the
           search that superseded it. */
        const on: typeof stream.on = (type, listener) =>
          stream.on(type, (chunk) => {
            if (!ctl.signal.aborted) {
              listener(chunk);
            }
          });

        on("start", () => {
          this.messageId = stream.messageId;
        });

        on("text-delta", () => {
          summary = stream.activeText;
          this.summary = summary;
          this.summaryParts = parseSummary(summary, results, true);
        });

        on("data-excerpt", (chunk) => {
          const next = insertResult(results, chunk.data);
          if (next === results) {
            return;
          }
          results = next;
          this.results = next;
          // Resolves any link that was waiting on this excerpt.
          this.summaryParts = parseSummary(summary, next, true);
        });

        on("data-status", (chunk) => {
          this.status = chunk.data.status;
        });

        // The server can report an error mid-stream without the request
        // failing. The partial response stays visible.
        on("error", (chunk) => {
          fail({ kind: "stream", message: chunk.errorText });
        });

        try {
          await stream.start();
        } catch (e) {
          /* Not `network`, which is the outer catch's business: the response
             headers arrived, so whatever went wrong here went wrong part way
             through a reply the user may already be reading. An abort lands
             here too, by erroring the response body; see the note on who owns
             the signal below. */
          fail({ kind: "stream", message: errorText(e) });
        }
      }
    } catch (e) {
      fail({ kind: "network", message: errorText(e) });
    } finally {
      if (!ctl.signal.aborted) {
        this.loading = false;
        // Re-parsed with streaming off, so a partial marker that never completed
        // shows as the literal text the model produced.
        this.summaryParts = parseSummary(summary, results, false);
        controller = null;
      }
    }
  },

  /** Clears the response, the input and the filters, and cancels anything in flight. */
  clear(): void {
    controller?.abort();
    controller = null;
    // The `loading: false` in here is load-bearing: the `finally` above skips
    // an aborted search, so nothing else would turn it off.
    Object.assign(this, emptyResponse());
    this.overallFeedbackStatus = "idle";
    this.query = "";
    // Emptied in place, not `params = emptyParams()`: replacing the object
    // would leave every `x-model` bound to a key that is no longer there.
    for (const [name, value] of Object.entries(this.params)) {
      this.params[name] = Array.isArray(value) ? [] : "";
    }
    this.close();
  },

  /* ---------- panels ---------- */

  toggle(name: Panel): void {
    this.panel = this.panel === name ? null : name;
  },

  close(): void {
    this.panel = null;
  },

  /* ---------- feedback ---------- */

  /**
   * Thumbs up or down, and the panel that a thumbs down opens.
   *
   * Replacing the whole feedback object is what makes the two mutually
   * exclusive: liking clears a previous dislike. Nothing here resets the reason
   * panel's draft message, because `x-if` destroys the panel and takes its
   * local state with it.
   */
  toggleFeedback(kind: "liked" | "disliked"): void {
    const on =
      kind === "liked" ? !!this.feedback.liked : !!this.feedback.disliked;

    // Written out rather than computed from `kind`, because
    // `{ [kind]: true, feedbackChoice: kind }` widens to an index signature
    // under a union key and will not satisfy `SearchFeedbackMetadata`.
    void this.sendFeedback(
      on
        ? {}
        : kind === "liked"
          ? { liked: true, feedbackChoice: "liked" }
          : { disliked: true, feedbackChoice: "disliked" },
    );

    this.panel = !on && kind === "disliked" ? "reason" : null;
  },

  /**
   * Records a dislike with a reason, and a message when the reason is "Other".
   *
   * Resolves to whether it sent, which is how the panel tracks its own status.
   * One POST per call, since each endpoint hit is a separate PostHog event and
   * submitting on selection as well as on send would double-count.
   */
  submitReason(choice: string, message = ""): Promise<boolean> {
    return this.sendFeedback({
      disliked: true,
      feedbackChoice: choice,
      feedbackMessage: choice === OTHER_REASON ? message : "",
    });
  },

  /**
   * The POST behind both of the above.
   *
   * `feedback` is written before the request and left in place if it fails, so
   * the thumb stays where the user put it and only `feedbackStatus` reports the
   * failure.
   */
  async sendFeedback(feedback: SearchFeedbackMetadata): Promise<boolean> {
    if (!this.messageId) {
      console.warn(
        "[ai-search] feedback ignored: no messageId yet. Wait for a search to start.",
      );
      return false;
    }

    this.feedback = feedback;
    this.feedbackStatus = "sending";

    const ok = await postJson("/api/feedback", {
      messageId: this.messageId,
      ...feedback,
    });

    this.feedbackStatus = ok ? "sent" : "error";
    return ok;
  },

  /** Session-level feedback. Unrelated to any one message, so no messageId. */
  async submitOverall(input: OverallFeedbackRequest): Promise<boolean> {
    this.overallFeedbackStatus = "sending";
    const ok = await postJson("/api/overall-feedback", input);

    this.overallFeedbackStatus = ok ? "sent" : "error";
    return ok;
  },

  /* ---------- analytics ----------
     Templates pass the whole object they rendered from, so there is nothing to
     look up: `results.php` passes the result, `summary.php` the part. */

  onResult(result: AiResultData): void {
    conversion({
      conversionType: "search-result",
      messageId: this.messageId,
      postId: result.id,
      postTitle: result.title,
      postPermalink: result.permalink,
      aiExcerpt: result.excerpt,
      aiRelevance: result.relevance,
    });
  },

  /**
   * A click on an inline summary link. The title sent is the one the model
   * wrote inline, which is what was clicked.
   */
  onSummaryLink(part: SummaryPart, event: Event): void {
    // The excerpt carrying this link's url has not arrived yet.
    if (part.type !== "link" || !part.permalink) {
      event.preventDefault();
      return;
    }
    conversion({
      conversionType: "inline-link",
      messageId: this.messageId,
      postId: part.id,
      postTitle: part.title,
      postPermalink: part.permalink,
      aiSummary: this.summary,
    });
  },

  /* ---------- derived ----------
     Conditions more than one block asks about. Left in the markup they drift,
     and they are what a template override is most likely to get subtly wrong.

     `lastQuery` is set as a search starts and cleared by `clear()`, and it is
     the only thing separating "has not searched" from "searched and got
     nothing". Without it one flag covers both and the page answers a finished
     search with "nothing searched yet". */

  get showSkeleton(): boolean {
    return this.loading && !this.summaryParts.length && !this.error;
  },

  get showFeedback(): boolean {
    return !!this.messageId && !this.loading;
  },

  /**
   * Nothing has been searched yet: the state the page loads in.
   *
   * No template the plugin ships renders anything for it, since "nothing
   * searched yet" where the answer goes is worse than an empty space. It exists
   * because `showNoResults` needs the distinction anyway, and so a theme that
   * does want a prompt can read it rather than re-derive it.
   */
  get showEmpty(): boolean {
    return !this.lastQuery && !this.loading && !this.error;
  },

  /**
   * A search finished and produced neither summary nor results. Not an error,
   * and it must not read as one. Mutually exclusive with `showEmpty` by the
   * `lastQuery` check and with `showSkeleton` by `loading`.
   */
  get showNoResults(): boolean {
    return (
      !!this.lastQuery &&
      !this.loading &&
      !this.error &&
      !this.summaryParts.length &&
      !this.results.length
    );
  },

  /**
   * The wording for `error`, which the store holds as a kind, a technical
   * message and an http status. Decided here rather than in error.php, so two
   * blocks cannot word one error differently and rewording needs no template
   * override.
   */
  get errorMessage(): string {
    if (!this.error) {
      return "";
    }
    switch (this.error.kind) {
      case "network":
        return "We could not reach the search service. Check your connection and try again.";
      case "http":
        return this.error.status === 429
          ? "That is a lot of searching. Give it a moment and try again."
          : "Something went wrong at our end. Please try again.";
      case "stream":
        return "The answer stopped part way through, so what you see may be incomplete.";
      default:
        return "Something went wrong.";
    }
  },
};

/*
 * Who owns the abort.
 *
 * The `AbortController` goes to `fetch` and only to `fetch`; `UIMessageStream`
 * takes a `ReadableStream` and nothing else. An abort stops it indirectly: the
 * browser errors the response body, and that error propagates through
 * `TextDecoderStream` -> `EventSourceParserStream` -> the `reader.read()` inside
 * `start()`, which rejects.
 *
 * Do not move the signal into `UIMessageStream`. Teardown already works, and it
 * would cost that module its ability to be driven by any plain
 * `ReadableStream`, which is how it is tested.
 */

function register(Alpine: AlpineType): void {
  Alpine.store("ai", store);

  /* Components below: only the blocks with state nothing else may see. Every
     other template gets a bare `x-data` and reads the store directly. */

  /**
   * The search box (form.php).
   *
   * No state of its own, since `query` is in the store. What has to be here is
   * `$refs.input`: refs are scoped to the component that declares them, so
   * putting the focus back after a clear cannot be hoisted.
   */
  Alpine.data("aiSearchForm", () => ({
    clear(this: any) {
      this.$store.ai.clear();
      this.$refs.input.focus();
    },
  }));

  /**
   * The "what went wrong?" panel, shown after a thumbs down (reason.php).
   *
   * Mounted with `x-if`, so the draft below cannot outlive the panel it was
   * typed into and no other block has to remember to reset it.
   *
   * `status` is local rather than the store's `feedbackStatus` because that one
   * flag also covers the thumbs above, so reading it here would light up
   * "Sending..." in the bar for a reason picked down here.
   */
  Alpine.data("aiReasonPanel", () => ({
    reason: "",
    message: "",
    status: "idle",

    /** The message as it read when a send last succeeded; "" if none has. */
    sentMessage: "",

    /**
     * Whether the message in the box is the one that was sent, which fills the
     * Submit button in. Not `status === "sent"`, which is also true of the
     * concrete reasons that send from `pick` without a message, so the button
     * would come up pre-filled on an unwritten "Other". Comparing the text also
     * un-fills it when the draft is edited after a send.
     */
    get delivered() {
      return !!this.sentMessage && this.message === this.sentMessage;
    },

    /**
     * Picking a concrete reason submits straight away; "Other" waits for the
     * message and its own button, so each user action is one request.
     */
    pick(this: any, value: string) {
      this.reason = value;
      // The last outcome described the reason picked then, so reporting it
      // against this one would be wrong.
      this.status = "idle";
      this.sentMessage = "";
      if (value !== this.$store.ai.otherReason) {
        void this.send(value);
      }
    },

    async send(this: any, choice: string, message = "") {
      this.status = "sending";
      const ok = await this.$store.ai.submitReason(choice, message);
      this.status = ok ? "sent" : "error";
      this.sentMessage = ok ? message : "";
    },
  }));

  /**
   * Session-level feedback: a rating, a comment, or both (overall.php).
   *
   * Local for the same reason as the reason panel's draft. Unlike that panel
   * this one reads the shared `overallFeedbackStatus`, since its endpoint has a
   * status flag to itself and no other block can make it say something untrue.
   */
  Alpine.data("aiOverallPanel", () => ({
    rating: null as number | null,
    comment: "",

    /** The rating and comment as they were sent, once a send has succeeded. */
    sent: null as { rating: number | null; comment: string } | null,

    get valid() {
      return this.rating !== null || this.comment.trim() !== "";
    },

    /**
     * Whether what is in the form is what was sent, which fills the Submit
     * button in and shows the thanks. Not the store's "sent", which outlives
     * this panel, so a reopen would congratulate an empty form, and which stays
     * true through an edit afterwards.
     */
    get delivered() {
      return (
        this.sent !== null &&
        this.sent.rating === this.rating &&
        this.sent.comment === this.comment
      );
    },

    async submit(this: any) {
      const attempt = { rating: this.rating, comment: this.comment };
      const ok = await this.$store.ai.submitOverall({
        rating: attempt.rating ?? undefined,
        comment: attempt.comment,
      });
      this.sent = ok ? attempt : null;
    },
  }));
}

document.addEventListener("alpine:init", () => {
  const Alpine = window.Alpine;
  if (!Alpine) {
    console.error(
      "[Ionata AI Search] alpine:init fired but window.Alpine is not set.",
    );
    return;
  }

  // Not at file scope: a page that enqueues this bundle and renders no
  // components should capture nothing.
  initAnalytics();

  register(Alpine);
  registered = true;
});

/*
 * Three ways the page comes up blank, and they want different fixes, so they
 * get different sentences. Every one of them looks identical in the browser:
 * `[x-cloak]` hides each block until Alpine un-cloaks it, and an Alpine that
 * never saw the store never un-cloaks anything.
 *
 * Alpine fires `alpine:initialized` at the end of `start()`, so reaching it
 * without having registered means Alpine ran and the handler above did not.
 * In practice that is the `alpine:init` early return, since a start that
 * happened before this file evaluated fires neither event and is caught on
 * `load` below.
 */
document.addEventListener("alpine:initialized", () => {
  if (!registered) {
    console.error(
      "[Ionata AI Search] Alpine started without registering the AI search " +
        "store, so the search components will not render. See the plugin README.",
    );
  }
});

/*
 * On `load`, because nothing else fires when the failure is that both Alpine
 * events are already in the past. Late rather than early is the point: a
 * deferred Alpine has certainly started by now, so neither branch can be a
 * false positive.
 *
 * The two branches are the whole reason this is not one message. Alpine on the
 * page means this bundle arrived after `start()` had already dispatched
 * `alpine:init` — an ordering problem, in the theme's enqueue. No Alpine at all
 * means nothing on the page ever loaded one — a missing enqueue. Reporting the
 * second for the first is what sent people looking for an Alpine that was
 * there all along.
 */
addEventListener("load", () => {
  if (registered) return;

  if (window.Alpine) {
    console.error(
      "[Ionata AI Search] this bundle loaded after Alpine had already " +
        "started, so it missed alpine:init and the search components will " +
        "not render. Load 'ionata-ai-search-store' before whatever calls " +
        "Alpine.start(). See the plugin README.",
    );
    return;
  }

  console.error(
    "[Ionata AI Search] Alpine.js never loaded, so the search components " +
      "will not render. The theme must enqueue Alpine; the plugin ships a " +
      "copy as 'ionata-ai-search-alpine'. See the plugin README.",
  );
});
